import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "@shared/styles/components/TooltipWrapper.module.css";

interface TooltipWrapperProps {
    children: ReactNode
    content: ReactNode
    placement?: "top" | "bottom" | "left" | "right"
    disabled?: boolean
    delayMs?: number
    className?: string
}

export const TooltipWrapper: React.FC<TooltipWrapperProps> = ({
    children,
    content,
    placement = "top",
    disabled = false,
    delayMs = 120,
    className
}) => {
    const tooltipId = useId();
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [showTimeout, setShowTimeout] = useState<number | null>(null);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

    const clearShowTimeout = () => {
        if (showTimeout !== null) {
            window.clearTimeout(showTimeout);
            setShowTimeout(null);
        }
    };

    const showTooltip = () => {
        if (disabled) {
            return;
        }

        clearShowTimeout();
        const timeoutId = window.setTimeout(() => {
            setIsVisible(true);
            setShowTimeout(null);
        }, delayMs);
        setShowTimeout(timeoutId);
    };

    const hideTooltip = () => {
        clearShowTimeout();
        setIsVisible(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            hideTooltip();
        }
    };

    useEffect(() => {
        return () => {
            if (showTimeout !== null) {
                window.clearTimeout(showTimeout);
            }
        };
    }, [showTimeout]);

    useLayoutEffect(() => {
        if (!isVisible) {
            return;
        }

        const updatePosition = () => {
            const wrapperElement = wrapperRef.current;
            const tooltipElement = tooltipRef.current;
            if (!wrapperElement || !tooltipElement) {
                return;
            }

            const wrapperRect = wrapperElement.getBoundingClientRect();
            const tooltipRect = tooltipElement.getBoundingClientRect();
            const spacing = 8;
            const viewportPadding = 12;

            let top = 0;
            let left = 0;
            switch (placement) {
                case "bottom":
                    top = wrapperRect.bottom + spacing;
                    left = wrapperRect.left + wrapperRect.width / 2;
                    break;
                case "left":
                    top = wrapperRect.top + wrapperRect.height / 2;
                    left = wrapperRect.left - spacing;
                    break;
                case "right":
                    top = wrapperRect.top + wrapperRect.height / 2;
                    left = wrapperRect.right + spacing;
                    break;
                case "top":
                default:
                    top = wrapperRect.top - spacing;
                    left = wrapperRect.left + wrapperRect.width / 2;
                    break;
            }

            const unclampedLeft = placement === "left"
                ? left - tooltipRect.width
                : placement === "right"
                    ? left
                    : left - tooltipRect.width / 2;

            const minLeft = viewportPadding;
            const maxLeft = window.innerWidth - tooltipRect.width - viewportPadding;
            const clampedLeft = Math.min(Math.max(unclampedLeft, minLeft), Math.max(minLeft, maxLeft));

            const unclampedTop = placement === "top"
                ? top - tooltipRect.height
                : placement === "bottom"
                    ? top
                    : top - tooltipRect.height / 2;

            const minTop = viewportPadding;
            const maxTop = window.innerHeight - tooltipRect.height - viewportPadding;
            const clampedTop = Math.min(Math.max(unclampedTop, minTop), Math.max(minTop, maxTop));

            setTooltipStyle({
                top: `${clampedTop}px`,
                left: `${clampedLeft}px`,
                transform: "none"
            });

            if (placement === "top" || placement === "bottom") {
                const offset = left - (clampedLeft + tooltipRect.width / 2);
                setTooltipStyle({
                    top: `${clampedTop}px`,
                    left: `${clampedLeft}px`,
                    transform: `translateX(${offset}px)`
                });
                return;
            }

            const verticalOffset = top - (clampedTop + tooltipRect.height / 2);
            setTooltipStyle({
                top: `${clampedTop}px`,
                left: `${clampedLeft}px`,
                transform: `translateY(${verticalOffset}px)`
            });
        };

        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isVisible, placement, content]);

    const tooltip = !disabled ? (
        <div
            id={tooltipId}
            ref={tooltipRef}
            role="tooltip"
            className={[
                styles.tooltip,
                styles[placement],
                isVisible ? styles.visible : ""
            ].filter(Boolean).join(" ")}
            style={tooltipStyle}
        >
            {content}
        </div>
    ) : null;

    return (
        <div
            ref={wrapperRef}
            className={[
                styles.wrapper,
                "DSTooltipWrapper",
                className
            ].filter(Boolean).join(" ")}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
            onKeyDown={handleKeyDown}
            aria-describedby={!disabled && isVisible ? tooltipId : undefined}
        >
            {children}
            {typeof document !== "undefined" && tooltip ? createPortal(tooltip, document.body) : null}
        </div>
    );
};

export default TooltipWrapper;
